/**
 * Login page. Two-phase: email/password, then an optional TOTP step when the
 * backend reports mfa_required.
 *
 * On success, redirects to the role-specific home via HOME_BY_ROLE (defaults to
 * the founder dashboard for unknown roles). The login() call surfaces backend
 * error codes as friendly messages, so caught errors are shown verbatim.
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import cegLogo from '../assets/CEG Logo.svg';

const HOME_BY_ROLE = {
  founder: '/dashboard',
  eir: '/eir',
  ceg: '/ceg',
  admin: '/admin',
};

/** Sign-in screen handling both the password step and the follow-up MFA code step. */
export default function Login() {
  const navigate = useNavigate();
  const { login, mfaLogin } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA second-step state
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfaToken(result.mfa_token);
        return;
      }
      navigate(HOME_BY_ROLE[result.role] || '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  }

  async function handleMFASubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await mfaLogin(mfaToken, mfaCode);
      navigate(HOME_BY_ROLE[user.role] || '/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid code. Please try again.');
      setMfaCode('');
    } finally {
      setLoading(false);
    }
  }

  // ── MFA step ──────────────────────────────────────────────────────────────
  if (mfaToken) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoWrap}>
            <img src={cegLogo} alt="CEG" style={styles.logoImg} />
          </div>
          <h1 style={styles.title}>Two-Factor Auth</h1>
          <p style={styles.subtitle}>Enter the 6-digit code from your authenticator app</p>

          <form onSubmit={handleMFASubmit} style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label} htmlFor="mfaCode">Verification Code</label>
              <input
                id="mfaCode"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                style={{ ...styles.input, letterSpacing: '0.25em', textAlign: 'center', fontSize: 22 }}
                placeholder="000000"
                required
                autoFocus
                autoComplete="one-time-code"
              />
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button type="submit" style={styles.submitBtn} disabled={loading || mfaCode.length !== 6}>
              {loading ? 'Verifying…' : 'Verify'}
            </button>
            <button
              type="button"
              style={{ ...styles.submitBtn, background: 'none', color: 'var(--color-forest)', border: '1.5px solid var(--color-mint-dark)', marginTop: 4 }}
              onClick={() => { setMfaToken(null); setMfaCode(''); setError(''); }}
            >
              Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Normal login ──────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logoWrap}>
          <img src={cegLogo} alt="CEG" style={styles.logoImg} />
        </div>
        <h1 style={styles.title}>Sign in</h1>
        <p style={styles.subtitle}>Venture Readiness Portal</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="you@example.com"
              required
              autoFocus
            />
          </div>

          <div style={styles.field}>
            <div style={styles.labelRow}>
              <label style={styles.label} htmlFor="password">Password</label>
              <button type="button" style={styles.showHideBtn} onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="••••••••"
              required
            />
          </div>

          {error && <p style={styles.error}>{error}</p>}

          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p style={styles.forgotLink}>
          <Link to="/forgot-password" style={styles.link}>Forgot your password?</Link>
        </p>

        <p style={styles.applyLink}>
          New to the program?{' '}
          <Link to="/apply" style={styles.link}>Apply here</Link>
        </p>

      </div>
    </div>
  );
}


const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-mint)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'var(--space-6)',
  },
  card: {
    background: 'var(--color-frost)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-modal)',
    padding: 'var(--space-10)',
    width: '100%',
    maxWidth: 400,
  },
  logoWrap: { display: 'flex', justifyContent: 'center', marginBottom: 'var(--space-5)' },
  logoImg: { width: 80, height: 80, objectFit: 'contain' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 30, fontWeight: 800, color: 'var(--color-forest)', textAlign: 'center', marginBottom: 'var(--space-1)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6, textAlign: 'center', marginBottom: 'var(--space-8)' },
  form: { display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' },
  field: { display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' },
  labelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  showHideBtn: { background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-forest)', opacity: 0.55, padding: 0 },
  label: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  input: {
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--color-mint-dark)',
    background: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-forest)',
    outline: 'none',
  },
  error: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-overdue)', textAlign: 'center', margin: 0 },
  submitBtn: {
    marginTop: 'var(--space-2)',
    padding: '12px',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-forest)',
    color: '#fff',
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  forgotLink: { fontFamily: 'var(--font-body)', fontSize: 15, textAlign: 'center', marginTop: 'var(--space-5)', marginBottom: 0, opacity: 0.75 },
  applyLink: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', textAlign: 'center', marginTop: 'var(--space-3)', opacity: 0.75 },
  link: { color: 'var(--color-forest)', fontWeight: 700 },
};
